import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from './snackbar.component';
import { TranslateService } from '@ngx-translate/core';

interface SnackbarQueueItem {
    message: string;
    title: string;
    action: string;
    duration: number;
    type: 'success' | 'error' | 'info' | 'warning';
}

@Injectable({
    providedIn: 'root'
})
export class SnackbarService {


    private queue: SnackbarQueueItem[] = [];
    private isShowing = false;

    constructor(private snackBar: MatSnackBar, private translateService: TranslateService) { }

    // ===============================================
    // =============== QUEUE HANDLING =================
    // ===============================================

    private enqueueSnackbar(item: SnackbarQueueItem) {
        this.queue.push(item);
        this.processQueue();
    }

    private processQueue() {
        if (this.isShowing || this.queue.length === 0) return;

        const item = this.queue.shift()!;
        this.isShowing = true;

        const ref = this.snackBar.openFromComponent(CustomSnackbarComponent, {
            data: {
                message: item.message,
                title: item.title,
                action: item.action,
                type: item.type
            },
            duration: item.duration,
            panelClass: ['custom-snackbar-container'],
            verticalPosition: 'top',
            horizontalPosition: 'right'
        });

        ref.afterDismissed().subscribe(() => {
            this.isShowing = false;
            this.processQueue();
        });
    }

    // ===============================================
    // ================== PUBLIC API ==================
    // ===============================================

    dismiss(): void {
        this.queue = [];
        this.snackBar.dismiss();
    }

    showSuccess(
        message: string,
        title: string = 'common.success',
        action: string = 'common.close',
        duration: number = 5000
    ): void {
        this.enqueueSnackbar({
            message,
            title: this.translateService.instant(title),
            action,
            duration,
            type: 'success'
        });
    }

    showError(
        message: string,
        title: string = 'common.error',
        action: string = 'common.close',
        duration: number = 5000
    ): void {
        this.enqueueSnackbar({
            message,
            title: this.translateService.instant(title),
            action,
            duration,
            type: 'error'
        });
    }

    showInfo(
        message: string,
        title: string = 'common.info',
        action: string = 'common.close',
        duration: number = 5000
    ): void {
        this.enqueueSnackbar({
            message,
            title: this.translateService.instant(title),
            action,
            duration,
            type: 'info'
        });
    }

    showWarning(
        message: string,
        title: string = 'common.warning',
        action: string = 'common.close',
        duration: number = 5000
    ): void {
        this.enqueueSnackbar({
            message,
            title: this.translateService.instant(title),
            action,
            duration,
            type: 'warning'
        });
    }


}
